// components/PortfolioSection.jsx
import React, { useState, useEffect } from 'react';
import { auth, db } from '../../../services/firebase';
import { addDoc, collection, serverTimestamp, query, where, getDocs, deleteDoc, doc, setDoc, onSnapshot } from 'firebase/firestore';
import useStockStore from '../../../stores/useStockStore';
import { useActiveBroker } from '../../../hooks/useActiveBroker';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useBrokerIntegration } from '../../../contexts/BrokerIntegrationContextCompat';
import StockItem from './StockItem';
import EditStockModal from './EditStockModal';
import { Trash2, Edit3 } from 'lucide-react';

const PortfolioSection = () => {
  const { 
    stocks, 
    setEditStock,
    resetStock,
    setStocks // เพิ่มฟังก์ชันนี้เพื่อบังคับอัปเดต UI
  } = useStockStore();
  const { notify } = useNotifications();
  const { brokerName } = useActiveBroker();
  const { hasConnections } = useBrokerIntegration();
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const totalPortfolioValue = stocks.reduce((total, stock) => total + (stock.value || 0), 0);

  // Real-time sync Strategy Center with tbl_engagement for current user
  // Only when a broker connection exists
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    // If not connected: clear local cache and reset UI, and do not listen
    if (!hasConnections) {
      try { localStorage.removeItem('strategyCenterData'); } catch {}
      if (typeof useStockStore.getState().resetAllStocks === 'function') {
        useStockStore.getState().resetAllStocks();
      }
      return;
    }

    const q = query(
      collection(db, 'tbl_engagement'),
      where('userId', '==', userId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        console.log('📊 PortfolioSection: Engagement snapshot received, docs:', snapshot.size);

        // If no engagement remains -> reset all symbols and clear local storage cache
        if (snapshot.empty) {
          console.log('📊 PortfolioSection: No engagement data, resetting stocks');
          if (typeof localStorage !== 'undefined') {
            try { localStorage.removeItem('strategyCenterData'); } catch {}
          }
          if (typeof useStockStore.getState().resetAllStocks === 'function') {
            useStockStore.getState().resetAllStocks();
          }
          // Force update UI by setting empty stocks array
          const emptyStocks = (useStockStore.getState().stocks || []).map(s => ({
            symbol: s.symbol,
            value: 0,
            type: null,
            hedge: false,
            status: '',
            quanlity: 0,
            spread: 1
          }));
          setStocks(emptyStocks);
          return;
        }

        // Build a mapping from symbol -> { quantity, status, type, spread }
        const bySymbol = new Map();
        snapshot.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const symbol = d.symbol;
          if (!symbol) return;
          // last write wins; Firestore snapshot is ordered by update time implicitly undefined
          bySymbol.set(symbol, {
            quantity: Number(d.maxPerTrade ?? d.maxpertrade ?? d.quantity) || 0,
            status: typeof d.status === 'string' ? d.status : '',
            type: d.type || null,
            spread: Number(d.spread) || (d.type === 'spread' ? 2 : 1)
          });
        });

        // Derive new stocks from current state, resetting those missing in mapping
        const nextStocks = (useStockStore.getState().stocks || []).map((s) => {
          const data = bySymbol.get(s.symbol);
          if (!data) {
            return { symbol: s.symbol, value: 0, type: null, hedge: false, status: '', quanlity: 0 };
          }
          return {
            ...s,
            value: data.quantity,
            quanlity: data.quantity,
            status: data.status,
            type: data.type,
            spread: data.spread
          };
        });

        // Only update if stocks actually changed
        const currentStocksJson = JSON.stringify(useStockStore.getState().stocks || []);
        const nextStocksJson = JSON.stringify(nextStocks);

        if (currentStocksJson === nextStocksJson) {
          console.log('📊 PortfolioSection: No stock changes detected, skipping update');
          return;
        }

        console.log('🔄 PortfolioSection: Updating stocks:', nextStocks.length);
        // Persist and update UI
        try { localStorage.setItem('strategyCenterData', JSON.stringify(nextStocks)); } catch {}
        setStocks(nextStocks);
      } catch (e) {
        console.warn('Strategy Center sync failed:', e);
      }
    });

    return () => unsubscribe();
  }, [hasConnections]);

  const handleEditClick = (stock) => {
    console.log('🖊 handleEditClick ->', stock);
    setEditStock(stock);
  };

  const handleRemoveClick = (stock) => {
    setSelectedStock(stock);
    setShowConfirmRemove(true);
  };

  const handleConfirmRemove = async () => {
    if (isRemoving) return;
    setIsRemoving(true);
    try {
      // Log the final removal action first (this is the final log before cleanup)
      await addDoc(collection(db, 'tbl_engagement'), {
        username: auth.currentUser?.displayName || auth.currentUser?.email || 'anonymous',
        userId: auth.currentUser?.uid || 'anonymous',
        broker: brokerName || 'Unknown',
        symbol: selectedStock.symbol,
        status: 'removed',
        maxPerTrade: selectedStock.quanlity || 0,
        timestamp: serverTimestamp()
      });
      console.log('Successfully logged Remove Agree to tbl_engagement');

      // Also log to tbl_notifications
      const agreeActionId = `${auth.currentUser?.uid || 'anonymous'}_${selectedStock.symbol}_remove_agree_${Date.now()}`;
      await setDoc(doc(db, 'tbl_notifications', agreeActionId), {
        log: `Remove ${selectedStock.symbol}: Agree`,
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'anonymous',
        symbol: selectedStock.symbol,
        status: 'Remove',
        action: 'Remove',
        stockStatus: selectedStock.status,
        stockType: selectedStock.type,
        stockQuanlity: selectedStock.quanlity,
        stockValue: selectedStock.value,
        actionId: agreeActionId
      });
      console.log('Successfully logged Remove Agree to tbl_notifications');

      // Reset the stock to its initial state
      console.log('🔄 Before resetStock - localStorage data:', localStorage.getItem('strategyCenterData'));
      resetStock(selectedStock.symbol);
      console.log('🔄 After resetStock - localStorage data:', localStorage.getItem('strategyCenterData'));
      
      setShowConfirmRemove(false);
      console.log(`Reset ${selectedStock.symbol} to initial state`);

      // Force verify and update localStorage if needed
      setTimeout(() => {
        const currentData = localStorage.getItem('strategyCenterData');
        if (currentData) {
          const parsed = JSON.parse(currentData);
          const targetStock = parsed.find(stock => stock.symbol === selectedStock.symbol);
          if (targetStock && (targetStock.value !== 0 || targetStock.status !== '' || targetStock.quanlity !== 0)) {
            console.log('🔧 Force correcting localStorage data for', selectedStock.symbol);
            const correctedStocks = parsed.map(stock => 
              stock.symbol === selectedStock.symbol 
                ? { symbol: selectedStock.symbol, value: 0, type: null, hedge: false, status: '', quanlity: 0 }
                : stock
            );
            localStorage.setItem('strategyCenterData', JSON.stringify(correctedStocks));
            console.log('✅ Forced localStorage correction completed');
            
            // Force refresh UI state to match localStorage
            setStocks(correctedStocks);
            console.log('🔄 Forced UI refresh completed');
          }
        }
      }, 500);

      // Delete all tbl_engagement records for this stock and user immediately
      // This removes the engagement from database completely
      try {
        const userId = auth.currentUser?.uid;
        if (userId && selectedStock) {
          console.log(`🗑️ Removing all tbl_engagement records for ${selectedStock.symbol} and user ${userId}`);
          
          const engagementQuery = query(
            collection(db, 'tbl_engagement'),
            where('userId', '==', userId),
            where('symbol', '==', selectedStock.symbol)
          );
          
          const engagementSnapshot = await getDocs(engagementQuery);
          const deletePromises = [];
          
          engagementSnapshot.forEach((docSnapshot) => {
            console.log(`🗑️ Deleting engagement record: ${docSnapshot.id}`);
            deletePromises.push(deleteDoc(doc(db, 'tbl_engagement', docSnapshot.id)));
          });
          
          await Promise.all(deletePromises);
          console.log(`✅ Successfully removed ${deletePromises.length} tbl_engagement records for ${selectedStock.symbol}`);
        }
      } catch (cleanupError) {
        console.error('Error during tbl_engagement removal:', cleanupError);
      }

    } catch (error) {
      console.error('Error during remove operation:', error);
    }
    setIsRemoving(false);
  };

  const handleCancelRemove = async () => {
    if (isRemoving) return;
    setIsRemoving(true);
    setShowConfirmRemove(false);

    // Log to Firebase
    try {
      await addDoc(collection(db, 'tbl_engagement'), {
        username: auth.currentUser?.displayName || auth.currentUser?.email || 'anonymous',
        userId: auth.currentUser?.uid || 'anonymous',
        broker: brokerName || 'Unknown',
        symbol: selectedStock.symbol,
        status: 'remove_cancelled',
        maxPerTrade: selectedStock.quanlity || 0,
        timestamp: serverTimestamp()
      });
      console.log('Successfully logged Remove Disagree to tbl_engagement');

      // Also log to tbl_notifications
      const disagreeActionId = `${auth.currentUser?.uid || 'anonymous'}_${selectedStock.symbol}_remove_disagree_${Date.now()}`;
      await setDoc(doc(db, 'tbl_notifications', disagreeActionId), {
        log: `Remove ${selectedStock.symbol}: Disagree`,
        timestamp: serverTimestamp(),
        userId: auth.currentUser?.uid || 'anonymous',
        symbol: selectedStock.symbol,
        status: 'Disagree',
        action: 'Disagree',
        stockStatus: selectedStock.status, // เพิ่ม status ของ stock
        stockType: selectedStock.type,
        stockQuanlity: selectedStock.quanlity,
        stockValue: selectedStock.value,
        actionId: disagreeActionId
      });
      console.log('Successfully logged Remove Disagree to tbl_notifications');
    } catch (error) {
      console.error('Error logging Remove Disagree to Firebase:', error);
    }
    setIsRemoving(false);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold">Strategy Center</h3>
        <div className="text-sm">
          <span className="text-gray-400">Total Value: </span>
          <span className="text-white font-medium">${totalPortfolioValue.toLocaleString()}</span>
        </div>
      </div>

      {hasConnections ? (
        <div className="space-y-3">
          {stocks
            .filter(stock => stock.value > 0 || stock.status !== '') // Only show engaged stocks
            .map((stock) => (
              <StockItem 
                key={stock.symbol}
                stock={stock} 
                onEdit={() => handleEditClick(stock)} 
                onRemove={() => handleRemoveClick(stock)} 
              />
            ))}
          {stocks.filter(stock => stock.value > 0 || stock.status !== '').length === 0 && (
            <div className="text-gray-400 text-sm bg-gray-800/40 border border-gray-700/30 rounded-md p-3">
              No stocks engaged. Add stocks to your Strategy Center to get started.
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-400 text-sm bg-gray-800/40 border border-gray-700/30 rounded-md p-3">
          Connect your broker to enable Strategy Center.
        </div>
      )}

      {showConfirmRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-xl w-full max-w-sm text-white">
            <h3 className="text-lg font-bold mb-4">Reset {selectedStock.symbol}?</h3>
            <p>Removing a stock from your Automated Portfolio does not mean Quantum Flo Auto Algo will sell your options or close any existing contracts. It simply means the bot will no longer trade this ETF moving forward. If you have an open position or options contract on a removed ETF, you'll be responsible for managing it manually.</p>

            <div className="flex justify-between mt-4 gap-4">
              <button onClick={handleCancelRemove} disabled={isRemoving} className={`w-full py-2 rounded transition-colors ${isRemoving ? 'bg-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500'}`}>Disagree</button>
              <button onClick={handleConfirmRemove} disabled={isRemoving} className={`w-full py-2 rounded transition-colors ${isRemoving ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500'}`}>Agree</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioSection;
